import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function focusableWithin(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
}

/**
 * Modal focus management: moves focus into `containerRef` when `active` (first `[data-autofocus]`, else the
 * first focusable not marked `[data-focus-skip]` — e.g. a header close button — else the container), keeps
 * Tab/Shift+Tab cycling inside, and restores focus to the previously focused element on deactivate.
 */
export function useFocusTrap(containerRef: RefObject<HTMLElement | null>, active: boolean): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) return;
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const initial =
      container.querySelector<HTMLElement>('[data-autofocus]') ??
      focusableWithin(container).find((el) => !el.hasAttribute('data-focus-skip')) ??
      container;
    initial.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      const items = focusableWithin(container);
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        event.preventDefault();
        return;
      }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      if (previous?.isConnected) previous.focus();
    };
  }, [containerRef, active]);
}

let scrollLocks = 0;

/** Prevents body scroll while any modal layer is open (reference counted for stacked layers). */
export function useScrollLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    scrollLocks += 1;
    if (scrollLocks === 1) document.body.style.overflow = 'hidden';
    return () => {
      scrollLocks -= 1;
      if (scrollLocks === 0) document.body.style.overflow = '';
    };
  }, [active]);
}

export type DismissReason = 'outside' | 'escape';

/**
 * Closes a non-modal popover on outside pointer-down or Escape. `refs` are the elements that count as
 * "inside" (typically the trigger and the popover). Escape is captured at the document and stopped, so an
 * open popover inside a Dialog closes without also closing the Dialog.
 */
export function useDismiss(
  refs: RefObject<HTMLElement | null>[],
  open: boolean,
  onDismiss: (reason: DismissReason) => void,
): void {
  // Latest values without re-subscribing listeners every render (callers pass inline arrays/closures).
  const latest = useRef({ refs, onDismiss });
  useLayoutEffect(() => {
    latest.current = { refs, onDismiss };
  });

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!latest.current.refs.some((ref) => ref.current?.contains(target))) latest.current.onDismiss('outside');
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        latest.current.onDismiss('escape');
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open]);
}

export type PopoverPlacement = {
  top: number;
  left: number;
  minWidth: number;
  /** True when rendered above the anchor because there was no room below. */
  flipped: boolean;
};

const GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Fixed-position coordinates for a popover anchored to `anchorRef`, rendered in a portal so table/card
 * `overflow` never clips it. Flips above the anchor when there is not enough room below; recomputes on
 * scroll and resize.
 */
export function usePopoverPosition(
  anchorRef: RefObject<HTMLElement | null>,
  popoverRef: RefObject<HTMLElement | null>,
  open: boolean,
  align: 'start' | 'end' = 'start',
): PopoverPlacement | null {
  const [placement, setPlacement] = useState<PopoverPlacement | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPlacement(null);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const popover = popoverRef.current;
      const height = popover?.offsetHeight ?? 0;
      const width = Math.max(popover?.offsetWidth ?? 0, rect.width);
      const spaceBelow = window.innerHeight - rect.bottom;
      const flipped = height > 0 && spaceBelow < height + GAP + VIEWPORT_MARGIN && rect.top > spaceBelow;
      const top = flipped ? rect.top - height - GAP : rect.bottom + GAP;
      const rawLeft = align === 'end' ? rect.right - width : rect.left;
      const left = Math.max(VIEWPORT_MARGIN, Math.min(rawLeft, window.innerWidth - width - VIEWPORT_MARGIN));
      setPlacement({ top, left, minWidth: rect.width, flipped });
    };
    update();
    // Second pass once the popover has measured content.
    const frame = requestAnimationFrame(update);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [anchorRef, popoverRef, open, align]);

  return placement;
}
